import { isPlatformBrowser } from '@angular/common';
import {
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, tap } from 'rxjs';
import { NotificationService } from '../services/notification.service';
import { AuthService } from '../services/auth.service';
import { homeUrl } from '../utils/view-access';

// Am Request festgemacht, nicht an router.url: beim Direktaufruf steht die Route beim Eintreffen
// der Antwort noch nicht fest.
const PUBLIC_API_PATHS = ['/timetable/public', '/classbook/public'];

// Ein 403 auf diesen Endpunkten (Pdf.Sign/Pdf.ManageCertificate-Policy) würde sonst unconditioniert
// zur Dashboard-Weiterleitung führen und dabei die PdfSignerStateService-Instanz zerstören (wird
// beim Routenwechsel entsorgt) - mitten in einer Signiersitzung gingen PDF, Signaturbild und
// Platzierung verloren. Die eigene Fehlerbehandlung in PdfSignerPageComponent zeigt die konkrete
// Meldung stattdessen direkt an, ohne die Seite zu verlassen.
const SESSION_PRESERVING_API_PATHS = ['/pdf/sign', '/pdf/certificate'];

// 403 = Teilnehmerportal nicht freigeschaltet; Meldung zeigt LoginComponent
const LOGIN_API_PATH = '/authentication/login';

export const AuthInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn,
): Observable<HttpEvent<any>> => {
  const router = inject(Router);
  const notificationService = inject(NotificationService);
  const authService = inject(AuthService);
  const inBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  const token = inBrowser ? authService.getToken() : null;

  const authReq = token
    ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
    : req;

  return next(authReq).pipe(
    tap({
      error: (error) => {
        if (!inBrowser) return;
        // Ohne Anmeldung gibt es kein Ziel für eine Weiterleitung; den Fehler zeigt der Aufrufer.
        if (PUBLIC_API_PATHS.some((path) => req.url.includes(path))) return;

        if (error.status === 401) {
          // sonst gilt der Benutzer für die Guards weiter als angemeldet
          if (token) authService.logout();
          router.navigate(['/login']);
        } else if (error.status === 403) {
          if (SESSION_PRESERVING_API_PATHS.some((path) => req.url.includes(path))) return;
          if (req.url.toLowerCase().includes(LOGIN_API_PATH)) return;
          router.navigateByUrl(homeUrl(authService));
          notificationService.error(
            'Zugriff verweigert. Keine Berechtigung für diese Aktion.',
          );
        }
      },
    }),
  );
};
